/**
 * Helpers for turning a layer's inline GeoJSON into per-feature display
 * data: a stable feature id (matching the backend's
 * ``SitePlanCandidateService._feature_id_for``), a human-readable name
 * pulled from the feature's properties, its geometry, and a projection
 * into SVG coordinates for the feature-map.
 *
 * Used by the Files-tab preview panel so the operator can SEE which
 * geometry "feature_0" actually refers to instead of guessing.
 */
import type { CandidateType, Layer } from "../../types/projects";

export interface PreviewFeature {
  /** Matches the backend feature_id: the feature's own ``id`` if present,
   * else the synthetic ``feature_<idx>`` form. Keys into
   * ``layer.feature_classifications``. */
  featureId: string;
  /** Best human label from properties (name / Name / title / label),
   * falling back to the featureId. */
  name: string;
  geometry: GeoGeometry | null;
}

type Position = [number, number];

export interface GeoGeometry {
  type: string;
  coordinates: unknown;
}

const NAME_KEYS = ["name", "Name", "NAME", "title", "Title", "label"];

/** Mirror of the backend's ``_feature_id_for`` so the ids the panel shows
 * line up 1:1 with what gets written to the candidate pool. */
function featureIdFor(feature: Record<string, unknown>, idx: number): string {
  const id = feature["id"];
  if (typeof id === "string" || typeof id === "number") return String(id);
  return `feature_${idx}`;
}

function featureName(
  properties: Record<string, unknown> | null,
  fallback: string,
): string {
  if (properties) {
    for (const key of NAME_KEYS) {
      const v = properties[key];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  return fallback;
}

/** Pull the ordered feature list out of a layer's inline GeoJSON. Returns
 * ``[]`` when the geometry lives in S3 (geojson_data null) or the shape
 * is unexpected. */
export function extractPreviewFeatures(layer: Layer): PreviewFeature[] {
  const payload = layer.geojson_data;
  if (!payload || typeof payload !== "object") return [];

  let rawFeatures: unknown[] = [];
  const type = (payload as Record<string, unknown>)["type"];
  if (type === "FeatureCollection") {
    const feats = (payload as Record<string, unknown>)["features"];
    rawFeatures = Array.isArray(feats) ? feats : [];
  } else if (type === "Feature") {
    rawFeatures = [payload];
  }

  return rawFeatures
    .filter((f): f is Record<string, unknown> => !!f && typeof f === "object")
    .map((feature, idx) => {
      const fid = featureIdFor(feature, idx);
      const props =
        feature["properties"] && typeof feature["properties"] === "object"
          ? (feature["properties"] as Record<string, unknown>)
          : null;
      const geom =
        feature["geometry"] && typeof feature["geometry"] === "object"
          ? (feature["geometry"] as GeoGeometry)
          : null;
      return { featureId: fid, name: featureName(props, fid), geometry: geom };
    });
}

export interface Extent {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Flatten every coordinate pair out of any GeoJSON geometry. */
function eachPosition(geom: GeoGeometry, fn: (p: Position) => void): void {
  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) return;
    if (
      node.length >= 2 &&
      typeof node[0] === "number" &&
      typeof node[1] === "number"
    ) {
      fn([node[0], node[1]]);
      return;
    }
    for (const child of node) walk(child);
  };
  walk(geom.coordinates);
}

/** Lon/lat extent across all features, falling back to ``layer.bounds``.
 * Returns null when there is nothing to draw. */
export function featuresExtent(
  features: PreviewFeature[],
  bounds: Layer["bounds"],
): Extent | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const f of features) {
    if (!f.geometry) continue;
    eachPosition(f.geometry, ([x, y]) => {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    });
  }
  if (minX === Infinity) {
    if (bounds) {
      const [bx0, by0, bx1, by1] = bounds;
      return { minX: bx0, minY: by0, maxX: bx1, maxY: by1 };
    }
    return null;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Build an SVG path/points string for a geometry, projected into a
 * ``width`` x ``height`` viewport with the given lon/lat extent. Latitude
 * is flipped (north up). A degenerate extent (single point) is padded so
 * the projection never divides by zero.
 *
 * Returns ``{ kind, d }`` where ``kind`` tells the caller which SVG
 * element to render: a filled ``path`` for polygons, a ``polyline`` for
 * lines, or a ``point`` (single "x,y") for points.
 */
export function projectGeometry(
  geom: GeoGeometry,
  extent: Extent,
  width: number,
  height: number,
  pad = 4,
): { kind: "path" | "polyline" | "point"; d: string } | null {
  const spanX = extent.maxX - extent.minX || 1e-9;
  const spanY = extent.maxY - extent.minY || 1e-9;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  // Preserve aspect ratio so shapes aren't stretched; center in the box.
  const scale = Math.min(innerW / spanX, innerH / spanY);
  const drawW = spanX * scale;
  const drawH = spanY * scale;
  const offX = pad + (innerW - drawW) / 2;
  const offY = pad + (innerH - drawH) / 2;

  const project = ([lon, lat]: Position): [number, number] => {
    const x = offX + (lon - extent.minX) * scale;
    const y = offY + (extent.maxY - lat) * scale; // flip Y (north up)
    return [x, y];
  };

  const ringToPath = (ring: unknown): string => {
    if (!Array.isArray(ring)) return "";
    const pts = ring
      .filter(
        (p): p is Position =>
          Array.isArray(p) &&
          typeof p[0] === "number" &&
          typeof p[1] === "number",
      )
      .map((p) => project(p));
    if (pts.length === 0) return "";
    const [first, ...rest] = pts;
    return (
      `M ${first[0].toFixed(1)} ${first[1].toFixed(1)} ` +
      rest.map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ") +
      " Z"
    );
  };

  switch (geom.type) {
    case "Polygon": {
      const rings = geom.coordinates as unknown[];
      const d = rings.map(ringToPath).filter(Boolean).join(" ");
      return d ? { kind: "path", d } : null;
    }
    case "MultiPolygon": {
      const polys = geom.coordinates as unknown[];
      const d = polys
        .flatMap((poly) => (Array.isArray(poly) ? poly.map(ringToPath) : []))
        .filter(Boolean)
        .join(" ");
      return d ? { kind: "path", d } : null;
    }
    case "LineString": {
      const coords = geom.coordinates as unknown[];
      const pts = coords
        .filter(
          (p): p is Position =>
            Array.isArray(p) &&
            typeof p[0] === "number" &&
            typeof p[1] === "number",
        )
        .map((p) => project(p));
      if (pts.length === 0) return null;
      return {
        kind: "polyline",
        d: pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" "),
      };
    }
    case "MultiLineString": {
      const lines = geom.coordinates as unknown[];
      const segs: string[] = [];
      for (const line of lines) {
        if (!Array.isArray(line)) continue;
        const pts = line
          .filter(
            (p): p is Position =>
              Array.isArray(p) &&
              typeof p[0] === "number" &&
              typeof p[1] === "number",
          )
          .map((p) => project(p));
        if (pts.length === 0) continue;
        const [first, ...rest] = pts;
        segs.push(
          `M ${first[0].toFixed(1)} ${first[1].toFixed(1)} ` +
            rest.map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`).join(" "),
        );
      }
      return segs.length ? { kind: "path", d: segs.join(" ") } : null;
    }
    case "Point": {
      const c = geom.coordinates as Position;
      if (typeof c?.[0] !== "number" || typeof c?.[1] !== "number") return null;
      const [x, y] = project(c);
      return { kind: "point", d: `${x.toFixed(1)},${y.toFixed(1)}` };
    }
    default:
      return null;
  }
}

/** Shared palette so the feature-map, the chips, and the legend agree. */
export const CANDIDATE_TYPE_COLORS: Record<CandidateType, string> = {
  boundary: "#7c3aed",
  exclusion: "#dc2626",
  road: "#d97706",
  poi: "#2563eb",
};
